<template>
  <transition name="fade">
    <div v-if="visible" class="gesture-float-window">
      <div class="status-bar" v-show="currentGesture">
        <i class="iconfont icon-xuanzhong"></i>
        <span>识别成功</span>
      </div>

      <div class="video-wrapper">
        <div v-if="isLoading" class="loading-mask">加载中...</div>
        <video ref="inputVideoRef" class="input-video" playsinline></video>
        <canvas ref="outputCanvasRef" width="180" height="120"></canvas>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, nextTick, onUnmounted, defineExpose, defineEmits } from 'vue';
import { Hands, HAND_CONNECTIONS } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';

// === 1. 定义事件 ===
const emit = defineEmits(['gesture-detected']);

// === 2. 响应式状态 ===
const visible = ref(false);
const isLoading = ref(true);
const currentGesture = ref('');
const canTrigger = ref(true);

// === 3. DOM 引用 ===
const inputVideoRef = ref(null);
const outputCanvasRef = ref(null);

// === 4. 内部实例 (不需要响应式，提升性能) ===
let camera = null;
let hands = null;

// === 5. 手势规则 ===
const gestureRules = [
  // {
  //   name: 'CLOSED_FIST', // 1. 握拳
  //   check: (lm) => {
  //      return lm[8].y > lm[6].y && lm[12].y > lm[10].y && 
  //             lm[16].y > lm[14].y && lm[20].y > lm[18].y;
  //   }
  // },
  {
    name: 'OK', // 2. OK
    check: (lm) => {
      const distance = getDistance(lm[4], lm[8]); // 拇指(4)和食指(8)距离
      // 其他三指伸直
      const othersOpen = lm[12].y < lm[10].y && lm[16].y < lm[14].y && lm[20].y < lm[18].y;
      return distance < 0.05 && othersOpen;
    }
  },
  // 其他手势规则可在此添加...
];

// === 6. 方法定义 ===

// 辅助：计算两点距离
const getDistance = (p1, p2) => {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
};

// 开启方法
const open = () => {
  visible.value = true;
  nextTick(() => {
    // 确保 DOM 渲染后再初始化
    if (!camera) initMediaPipe();
    else camera.start();
  });
};

// 关闭方法
const close = () => {
  visible.value = false;
  
  // 1. 停止处理
  if (camera) {
    camera.stop();
  }
  
  // 2. 【关键】销毁实例，强制下次重新初始化
  camera = null;
  isLoading.value = true;
  currentGesture.value = '';
};

// 初始化 MediaPipe
const initMediaPipe = () => {
  if (!inputVideoRef.value || !outputCanvasRef.value) return;

  const canvasCtx = outputCanvasRef.value.getContext('2d');

  hands = new Hands({
    locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`
  });

  hands.setOptions({
    maxNumHands: 1,
    modelComplexity: 1,
    minDetectionConfidence: 0.5,
    minTrackingConfidence: 0.5
  });

  hands.onResults((results) => {
    if (!visible.value) return;

    isLoading.value = false;
    canvasCtx.save();
    canvasCtx.clearRect(0, 0, outputCanvasRef.value.width, outputCanvasRef.value.height);
    
    // 绘制摄像头画面
    canvasCtx.drawImage(results.image, 0, 0, outputCanvasRef.value.width, outputCanvasRef.value.height);

    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      const landmarks = results.multiHandLandmarks[0];
      
      // 绘制骨骼
      drawConnectors(canvasCtx, landmarks, HAND_CONNECTIONS, { color: '#00FF00', lineWidth: 1 });
      drawLandmarks(canvasCtx, landmarks, { color: '#FF0000', lineWidth: 0.5, radius: 1 });
      
      detectGestures(landmarks);
    }
    canvasCtx.restore();
  });

  camera = new Camera(inputVideoRef.value, {
    onFrame: async () => {
      if (visible.value && hands) {
        await hands.send({ image: inputVideoRef.value });
      }
    },
    width: 640,
    height: 480
  });

  camera.start();
};

// 手势检测逻辑
const detectGestures = (landmarks) => {
  // === 几何过滤 (防止脸部误判) ===
  const wrist = landmarks[0];
  const middleTip = landmarks[12];
  const handSize = getDistance(wrist, middleTip);

  // 这里的 0.35 对应 180x120 的小框可能需要微调，但在逻辑坐标系中通常是归一化的
  // 如果发现容易误判，可以调整这个数值
  if (handSize > 0.35) {
      currentGesture.value = 'TOO_LARGE'; 
      return; 
  }

  let detectedName = '';
  for (let rule of gestureRules) {
    if (rule.check(landmarks)) {
      detectedName = rule.name;
      break;
    }
  }

  if (detectedName && detectedName !== currentGesture.value && canTrigger.value) {
      currentGesture.value = detectedName;
      triggerEvent(detectedName);
  } else if (!detectedName) {
      currentGesture.value = '';
  }
};

// 触发事件
const triggerEvent = (gestureName) => {
  emit('gesture-detected', gestureName);
  canTrigger.value = false;
  setTimeout(() => {
    canTrigger.value = true;
  }, 1500); // 冷却时间
};

// 组件卸载时清理
onUnmounted(() => {
  if (camera) {
    camera.stop();
    camera = null;
  }
});

// === 7. 暴露方法供外部调用 ===
defineExpose({
  open,
  close
});
</script>

<style scoped lang="scss">
/* ==================== 样式保持不变 ==================== */
.gesture-float-window {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 180px;
  height: auto;
  
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  
  font-family: sans-serif;
  color: white;
  padding: 5px;
  overflow: hidden;
}

.status-bar {
  width: 114px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  color: #21C237;
  border: solid 1px #21C237;
  box-sizing: border-box;
  box-shadow: 0px 0px 4px 0px rgba(33, 194, 55, 0.5);
  background-color: rgba(255, 255, 255, 0.9);
  
  .iconfont {
    margin-right: 10px;
    color: #21C237;
  }
}

.video-wrapper {
  position: relative;
  width: 180px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  background: #333;
  transform: scaleX(-1);
  flex-shrink: 0;
}

.input-video { display: none; }
canvas { width: 100%; height: 100%; display: block; }

.loading-mask {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; justify-content: center; align-items: center;
  transform: scaleX(-1);
  font-size: 12px;
  background: rgba(0,0,0,0.6);
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>